import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

import { Paths } from "@/core/constants/paths";
import { limparConsultas, manterConsultasEmCache } from "@/ds";
import { OBRA_CONSULTAS_PREFIXO } from "@/modules/obras";
import { NOVA_OBRA_ETAPAS } from "./hooks/nova-obra.types";
import { useNovaObra } from "./hooks/nova-obra.context";
import { isNovaObraEntrada } from "@/screens/obras/rota-nova-obra";
import type { NovaObraRotaContexto } from "@/screens/obras/rota-nova-obra";

/** Aviso nativo de saída (F5/fechar a aba) enquanto houver alterações não salvas. */
function useBeforeUnloadWarning(active: boolean) {
  useEffect(() => {
    if (!active) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);
}

/** Overlays PrimeReact abertos (lista do select, menu, calendário...) que consomem o Esc. */
const OVERLAYS_ABERTOS = [
  ".p-dropdown-panel",
  ".p-multiselect-panel",
  ".p-datepicker",
  ".p-autocomplete-panel",
  ".p-cascadeselect-panel",
  ".p-treeselect-panel",
  ".p-overlaypanel",
  ".p-menu-overlay",
  ".p-tieredmenu-overlay",
].join(",");

/**
 * Esc fecha o wizard (pede confirmação) só quando nada mais o consome. O Dialog do Prime escuta o
 * Esc no `document` e fechava o wizard junto com o select aberto; aqui o Esc que fecha um overlay
 * (ou outro diálogo por cima) não chega ao wizard.
 */
function useEscapeParaCancelar(active: boolean, onEscape: () => void) {
  useEffect(() => {
    if (!active) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (document.querySelector(OVERLAYS_ABERTOS)) return;
      // Outro diálogo modal aberto por cima (alterar dominante, confirmações) trata o próprio Esc.
      if (document.querySelectorAll(".p-dialog-mask").length > 1) return;
      onEscape();
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [active, onEscape]);
}

export function useNovaObraScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const rota = useOutletContext<NovaObraRotaContexto | null>();
  const veioDaLista = isNovaObraEntrada(location.state);
  const { state, isDirty, avancar, voltar, irParaEtapa } = useNovaObra();
  const [confirmandoSaida, setConfirmandoSaida] = useState(false);
  const guardaEquipe = useRef<(() => boolean) | null>(null);
  const [verificandoEquipe, setVerificandoEquipe] = useState(false);

  useBeforeUnloadWarning(isDirty);

  // Ir e voltar entre as etapas não refaz as consultas: o cache vale enquanto a Nova Obra está aberta.
  useEffect(() => manterConsultasEmCache(OBRA_CONSULTAS_PREFIXO), []);

  const fecharConfirmacaoSaida = useCallback(() => setConfirmandoSaida(false), []);

  const iniciarVerificacaoEquipe = useCallback(() => setVerificandoEquipe(true), []);
  const concluirVerificacaoEquipe = useCallback(() => setVerificandoEquipe(false), []);
  // A etapa Equipe avisa cada mudança como booleano: aqui vira início ou fim da verificação.
  const acompanharVerificacaoEquipe = useCallback(
    (verificando: boolean) =>
      verificando ? iniciarVerificacaoEquipe() : concluirVerificacaoEquipe(),
    [iniciarVerificacaoEquipe, concluirVerificacaoEquipe],
  );

  // Aberta pela lista, sair volta no histórico (o mesmo que o voltar do navegador); por link direto, a lista toma o lugar.
  // Cancelar e concluir passam por aqui: a próxima abertura consulta tudo de novo.
  const sair = useCallback(() => {
    limparConsultas(OBRA_CONSULTAS_PREFIXO);
    if (veioDaLista) navigate(-1);
    else navigate(Paths.OBRAS, { replace: true });
  }, [navigate, veioDaLista]);

  // A obra nova entra na lista do fundo e o toast sobe no host da Lista, que segue na tela depois que o modal fecha.
  const obraCriada = useCallback(
    (mensagem: string, detalhe?: string) => {
      rota?.recarregarLista();
      rota?.avisarSucesso(mensagem, detalhe);
    },
    [rota],
  );

  // Cancelar/X: com alterações pede confirmação; sem alterações sai direto.
  const cancelar = useCallback(() => {
    if (isDirty) setConfirmandoSaida(true);
    else sair();
  }, [isDirty, sair]);

  // Etapa 4: a equipe valida as alocações e avisa de papéis obrigatórios ausentes antes de avançar.
  const proximaEtapa = useCallback(() => {
    if (state.etapaAtual === 4 && guardaEquipe.current?.()) return;
    avancar();
  }, [state.etapaAtual, avancar]);

  const primeiraEtapa = state.etapaAtual === 1;
  const ultimaEtapa = state.etapaAtual >= 5;

  useEscapeParaCancelar(!confirmandoSaida, cancelar);

  // Passos do stepper: `done` são as etapas anteriores à atual (no mobile trocam o número pelo check) e só elas, se já
  // alcançadas, permitem voltar sem validar nem perder dados (RN09); `ariaLabel` dá nome ao botão quando o título some.
  const etapas = NOVA_OBRA_ETAPAS.map(({ numero, titulo }) => {
    const done = numero < state.etapaAtual;
    return {
      numero,
      titulo,
      situacao: done
        ? ("done" as const)
        : numero === state.etapaAtual
          ? ("active" as const)
          : ("pending" as const),
      ariaLabel: `Etapa ${numero}: ${titulo}${done ? ", concluída" : ""}`,
      testId: `nova-obra-step-${numero}`,
      onSelect: done && numero <= state.maiorEtapaAlcancada ? () => irParaEtapa(numero) : undefined,
    };
  });

  return {
    etapas,
    state,
    irParaEtapa,
    voltar,
    cancelar,
    sair,
    obraCriada,
    concluir: sair,
    proximaEtapa,
    primeiraEtapa,
    ultimaEtapa,
    confirmandoSaida,
    fecharConfirmacaoSaida,
    guardaEquipe,
    verificandoEquipe,
    iniciarVerificacaoEquipe,
    concluirVerificacaoEquipe,
    acompanharVerificacaoEquipe,
  };
}
